import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Box, Button, Divider, Grid, Modal, ModalProps, NumberInput} from "@mantine/core";
import {useCallback, useMemo, useRef, useState} from "react";
import {useGeofenceForm} from "./useGeofenceForm";
import {EntityIdentifier, GeofenceTriggerType, toGeofenceTriggerType} from "@ots-frontend/ots-data";
import {GeofenceTriggerTypeSelect} from "../../select/entity/GeofenceTriggerTypeSelect/GeofenceTriggerTypeSelect";
import {Circle} from "@react-google-maps/api";
import {Marker} from "../../map/marker/Marker/Marker";
import {Map, MAP_DEFAULT_CENTER, useMapLoader} from "../../map/Map/Map";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";

export interface GeofenceFormProps {
  options?: GeofenceFormOptions;
  defaultValues?: GeofenceFormDefaultValues;
  onSubmit?: (values: GeofenceFormValues) => void;
  submitting?: boolean;
}

export interface GeofenceFormOptions {
  regionId?: string;
  objectSelectDisabled?: boolean
  googleMapsApiKey?: string;
}

export interface GeofenceFormValues {
  triggerType: GeofenceTriggerType
  latCoord: number
  longCoord: number
  radius: number
  object: EntityIdentifier
}

export interface GeofenceFormDefaultValues extends Partial<Omit<GeofenceFormValues, 'object'>> {
  object?: Partial<EntityIdentifier>
}

export interface GeofenceFormRawValues {
  triggerType?: string
  latCoord?: number
  longCoord?: number
  radius?: number
  objectId?: string
  objectType?: string
}

export type GeofenceFormType = UseFormReturnType<GeofenceFormRawValues, (values: GeofenceFormRawValues) => GeofenceFormRawValues>

export const GeofenceForm = (props: GeofenceFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false,
  } = props;

  const circleRef = useRef<google.maps.Circle | null>(null);
  const [_map, setMap] = useState<google.maps.Map | null>(null);

  const {t} = useTranslation(["common"]);

  const form = useGeofenceForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        triggerType: toGeofenceTriggerType(values.triggerType!),
        latCoord: values.latCoord!,
        longCoord: values.longCoord!,
        radius: values.radius!,
        object: {
          id: values.objectId!,
          type: values.objectType!,
        },
      });
    }
  }, [form, onSubmit]);

  const {isLoaded: isMapLoaded} = useMapLoader({
    googleMapsApiKey: options?.googleMapsApiKey ?? "",
  });

  const center: google.maps.LatLngLiteral = useMemo(() => {
    const formValues = form.getTransformedValues()
    if (typeof formValues.latCoord !== 'undefined' && typeof formValues.longCoord !== 'undefined') {
      const lat = formValues.latCoord
      const lng = formValues.longCoord

      if (lat !== 0 && lng !== 0) {
        return {
          lat: lat,
          lng: lng,
        }
      }
    }

    return MAP_DEFAULT_CENTER
  }, [form])

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onMapUnmount = useCallback((map: google.maps.Map) => {
    setMap(null);
  }, []);

  const onMapMarkerDrag = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      form.setFieldValue("latCoord", e.latLng.lat())
      form.setFieldValue("longCoord", e.latLng.lng())
    }
  }, [form]);

  const onMapCircleRadiusChanged = useCallback(() => {
    const circle = circleRef.current;
    if (circle) {
      form.setFieldValue("radius", circle.getRadius())
    }
  }, [form, circleRef]);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    const formValues = form.getTransformedValues()
    if (typeof formValues.latCoord === 'undefined' && typeof formValues.longCoord === 'undefined') {
      if (e.latLng) {
        form.setFieldValue("latCoord", e.latLng.lat())
        form.setFieldValue("longCoord", e.latLng.lng())
      }
    }
  }, [form]);

  return (
    <Grid grow gutter="md">
      <Grid.Col span={6}>
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <Grid grow gutter="xs">
            <Grid.Col span={12}>
              <GeofenceTriggerTypeSelect
                size="xs"
                label={t("common.entity.geofence.form.field.trigger", "Trigger")}
                placeholder={t<string>("common.entity.geofence.form.field.trigger", "Trigger")}
                clearable
                withAsterisk
                required
                {...form.getInputProps("triggerType")} />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={t("common.entity.geofence.form.field.lat", "Latitude")}
                placeholder={t<string>("common.entity.geofence.form.field.lat", "Latitude")}
                precision={6}
                min={-90}
                max={90}
                step={0.000001}
                hideControls
                {...form.getInputProps("latCoord")} />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={t("common.entity.geofence.form.field.long", "Longitude")}
                placeholder={t<string>("common.entity.geofence.form.field.long", "Longitude")}
                precision={6}
                min={-180}
                max={180}
                step={0.000001}
                hideControls
                {...form.getInputProps("longCoord")} />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label={t("common.entity.geofence.form.field.radius", "Radius (meters)")}
                placeholder={t<string>("common.entity.geofence.form.field.radius", "Radius (meters)")}
                precision={0}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                withAsterisk
                required
                {...form.getInputProps("radius")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="sm" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={12}>
              <EntityTypeSelect
                disabled={options?.objectSelectDisabled}
                options={['SITE']}
                label={t("common.entity.geofence.form.field.object", "Object")}
                placeholder={t<string>("common.entity.geofence.form.field.object", "Object")}
                withAsterisk
                required
                clearable
                {...form.getInputProps("objectType")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <EntitySelect
                pt="md"
                disabled={options?.objectSelectDisabled}
                entityType={form.getTransformedValues().objectType}
                regionId={options?.regionId}
                withAsterisk
                required
                clearable
                {...form.getInputProps("objectId")} />
            </Grid.Col>
          </Grid>
          <Divider my="sm" variant="dotted"/>
          <Button type="submit" loading={submitting} fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </form>
      </Grid.Col>
      <Grid.Col span={6}>
        <Grid grow gutter="xs">
          {isMapLoaded &&
            <Grid.Col span={12}>
              <Box
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <Map
                  onLoad={onMapLoad}
                  onUnmount={onMapUnmount}
                  zoom={15}
                  center={center}
                  mapContainerStyle={{width: "100%", height: '400px'}}
                  options={{
                    mapTypeId: "satellite",
                    streetViewControl: false
                  }}
                  onClick={onMapClick}
                >
                  {center &&
                    <>
                      <Circle
                        onLoad={(circle) => (circleRef.current = circle)}
                        center={center}
                        radius={form.getTransformedValues().radius ?? 0}
                        editable={true}
                        onRadiusChanged={onMapCircleRadiusChanged}/>
                      <Marker
                        position={center}
                        draggable={true}
                        onDragEnd={onMapMarkerDrag}/>
                    </>
                  }
                </Map>
              </Box>
            </Grid.Col>
          }
        </Grid>
      </Grid.Col>
    </Grid>
  );
};

interface GeofenceFormModalProps extends GeofenceFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const GeofenceFormModal = (props: GeofenceFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <GeofenceForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
